import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { ApiService } from '@/app/services/api.service';
import { NotificationService } from '@/app/services/notification.service';
import { handleApiError } from '@/app/utils/api-error-handler';
import * as config from '@/lib-backend/methods/config';
import { Company, RealmConfig } from '@/lib-backend';

export type CreateCompanyOutcome =
    | { result: 'Ok'; company: Company }
    | { result: 'NameAlreadyUsed' | 'Unknown' };

export type UpdateCompanyOutcome =
    | { result: 'Ok'; company: Company }
    | { result: 'NotFound' | 'NameAlreadyUsed' | 'InvalidActivePaymentMethod' | 'Unknown' };

export type DeleteCompanyOutcome = config.DeleteCompanyResult;

export type ReassignRealmOutcome =
    | { result: 'Ok'; realmConfig: RealmConfig }
    | { result: 'RealmNotFound' | 'CompanyNotFound' | 'CompanyDeleted' | 'InvalidActivePaymentMethod' | 'Unknown' };

@Injectable({ providedIn: 'root' })
export class CompanyService {
    private readonly api = inject(ApiService);
    private readonly router = inject(Router);
    private readonly notification = inject(NotificationService);

    readonly companies = signal<Company[]>([]);
    readonly loading = signal(false);
    readonly error = signal<string | null>(null);

    /** Map of CompanyId → Company for fast lookup. */
    readonly byId = computed(() => {
        const map = new Map<string, Company>();
        for (const c of this.companies()) map.set(c.Id, c);
        return map;
    });

    async loadCompanies(excludeDeleted = true): Promise<void> {
        this.loading.set(true);
        this.error.set(null);
        try {
            const resp = await this.api.client.call(
                config.listCompanies({ ExcludeDeleted: excludeDeleted })
            );
            this.companies.set(resp.Companies);
        } catch (e) {
            this.error.set('Failed to load companies.');
            handleApiError(e, this.router, this.notification);
        } finally {
            this.loading.set(false);
        }
    }

    async getCompany(companyId: string): Promise<Company | null> {
        try {
            const resp = await this.api.client.call(
                config.getCompany({ CompanyId: companyId })
            );
            return resp.Result === 'Ok' ? resp.Company : null;
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return null;
        }
    }

    async createCompany(params: config.CreateCompanyRequest): Promise<CreateCompanyOutcome> {
        try {
            const resp = await this.api.client.call(
                config.createCompany({
                    ...params,
                    Country: params.Country.toUpperCase(),
                })
            );
            if (resp.Result === 'Ok') {
                await this.loadCompanies();
                return { result: 'Ok', company: resp.Company };
            }
            return { result: resp.Result };
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return { result: 'Unknown' };
        }
    }

    async updateCompany(params: config.UpdateCompanyRequest): Promise<UpdateCompanyOutcome> {
        try {
            const resp = await this.api.client.call(
                config.updateCompany({
                    ...params,
                    CountryCode: params.CountryCode ? params.CountryCode.toUpperCase() : params.CountryCode,
                })
            );
            if (resp.Result === 'Ok') {
                await this.loadCompanies();
                return { result: 'Ok', company: resp.Company };
            }
            return { result: resp.Result };
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return { result: 'Unknown' };
        }
    }

    async deleteCompany(companyId: string): Promise<DeleteCompanyOutcome> {
        try {
            const resp = await this.api.client.call(
                config.deleteCompany({ CompanyId: companyId })
            );
            if (resp.Result === 'Ok') {
                await this.loadCompanies();
            }
            return resp.Result;
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return 'Unknown';
        }
    }

    async reassignRealm(realmId: string, companyId: string): Promise<ReassignRealmOutcome> {
        try {
            const resp = await this.api.client.call(
                config.reassignRealmToCompany({ RealmId: realmId, CompanyId: companyId })
            );
            if (resp.Result === 'Ok') {
                return { result: 'Ok', realmConfig: resp.RealmConfig };
            }
            return { result: resp.Result };
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return { result: 'Unknown' };
        }
    }
}
